import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams, IRowNode } from 'ag-grid-community';
import { DynamicGridConfigItem } from '../services/carrier-orders-grid/carrier-orders-grid.service';

interface FlagDisplayItem {
  config: DynamicGridConfigItem;
  dbField: string;
  label: string;
  shortLabel: string;
  tone: 'blue' | 'amber' | 'emerald' | 'violet' | 'slate';
  state: 'off' | 'on' | 'not-required' | 'required' | 'required-set';
  iconClass: string;
  iconState: 'is-x' | 'is-check' | 'is-neutral' | 'is-required';
  value: boolean;
  editable: boolean;
}

export interface FlagsCellRendererParams extends ICellRendererParams {
  flagsConfig: DynamicGridConfigItem[];
  onToggleFlag: (ctx: {
    rowData: Record<string, any>;
    node: IRowNode<any> | null;
    col: DynamicGridConfigItem;
    dbField: string;
    newValue: boolean;
  }) => void;
}

@Component({
  selector: 'app-flags-cell-renderer',
  template: `
    <div class="gfr" [class.gfr--empty]="!flags.length">
      <button
        *ngFor="let flag of flags"
        type="button"
        class="gfr__flag"
        [ngClass]="[
          'gfr__flag--' + flag.tone,
          'is-' + flag.state,
          flag.editable ? 'is-editable' : 'is-readonly'
        ]"
        [title]="getTitle(flag)"
        [disabled]="!flag.editable"
        (click)="toggle(flag, $event)"
      >
        <span class="gfr__icon" [ngClass]="flag.iconState">
          <i class="fa-solid" [ngClass]="flag.iconClass"></i>
        </span>
        <span class="gfr__label">{{ flag.shortLabel }}</span>
      </button>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
    }

    .gfr {
      display: flex;
      align-items: center;
      gap: 0.3rem;
      width: 100%;
      min-width: 0;
      overflow: hidden;
    }

    .gfr__flag {
      display: inline-flex;
      align-items: center;
      gap: 0.42rem;
      min-width: 0;
      height: 26px;
      padding: 0 0.56rem 0 0.28rem;
      border: 1px solid rgba(148, 163, 184, 0.16);
      border-radius: 999px;
      background: rgba(248, 250, 252, 0.02);
      color: var(--cwo-text-secondary, #64748b);
      font-size: 0.64rem;
      font-weight: 700;
      letter-spacing: 0.015em;
      white-space: nowrap;
      cursor: default;
      transition: border-color 0.16s ease, background 0.16s ease, color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease;
    }

    .gfr__flag.is-editable {
      cursor: pointer;
    }

    .gfr__flag.is-editable:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
    }

    .gfr__flag.is-on {
      background: rgba(34, 197, 94, 0.12);
      border-color: rgba(34, 197, 94, 0.24);
      color: var(--cwo-text-main, #0f172a);
    }

    .gfr__flag.is-off {
      background: rgba(148, 163, 184, 0.08);
      border-color: rgba(148, 163, 184, 0.18);
      color: var(--cwo-text-secondary, #64748b);
    }

    .gfr__flag.is-not-required {
      background: rgba(239, 68, 68, 0.08);
      border-color: rgba(239, 68, 68, 0.22);
      color: var(--cwo-text-secondary, #64748b);
    }

    .gfr__flag.is-required {
      background: rgba(245, 158, 11, 0.14);
      border-color: rgba(245, 158, 11, 0.34);
      color: #f59e0b;
      box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.08);
    }

    .gfr__flag.is-required-set {
      background: rgba(34, 197, 94, 0.12);
      border-color: rgba(34, 197, 94, 0.28);
      color: var(--cwo-text-main, #0f172a);
    }

    .gfr__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1rem;
      height: 1rem;
      border-radius: 999px;
      font-size: 0.56rem;
      flex: 0 0 auto;
    }

    .gfr__icon.is-check {
      background: #22c55e;
      color: #fff;
      box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.14);
    }

    .gfr__icon.is-x {
      background: rgba(239, 68, 68, 0.12);
      color: #ef4444;
      border: 1px solid rgba(239, 68, 68, 0.18);
    }

    .gfr__icon.is-neutral {
      background: rgba(148, 163, 184, 0.14);
      color: #94a3b8;
      border: 1px solid rgba(148, 163, 184, 0.2);
    }

    .gfr__icon.is-required {
      background: #f59e0b;
      color: #111827;
      box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.16);
    }

    .gfr__label {
      overflow: hidden;
      text-overflow: ellipsis;
      color: inherit;
      max-width: 2.8rem;
    }
  `],
})
export class FlagsCellRendererComponent implements ICellRendererAngularComp {
  flags: FlagDisplayItem[] = [];
  private params!: FlagsCellRendererParams;

  agInit(params: FlagsCellRendererParams): void {
    this.params = params;
    this.flags = this.buildFlags(params);
  }

  refresh(params: FlagsCellRendererParams): boolean {
    this.params = params;
    this.flags = this.buildFlags(params);
    return true;
  }

  toggle(flag: FlagDisplayItem, event: MouseEvent): void {
    event.stopPropagation();

    if (!flag.editable || !this.params.onToggleFlag || !this.params.data) {
      return;
    }

    this.params.onToggleFlag({
      rowData: this.params.data,
      node: this.params.node ?? null,
      col: flag.config,
      dbField: flag.dbField,
      newValue: !flag.value,
    });
  }

  getTitle(flag: FlagDisplayItem): string {
    if (flag.state === 'not-required') {
      return 'Technician: Not required for this carrier, warehouse, or return destination.';
    }

    if (flag.state === 'required') {
      return 'Technician: Required, but no technician is assigned.';
    }

    if (flag.state === 'required-set') {
      return 'Technician: Required and assigned.';
    }

    const state = flag.value ? 'Yes' : 'No';
    return flag.editable
      ? `${flag.label}: ${state}. Click to toggle.`
      : `${flag.label}: ${state}.`;
  }

  private buildFlags(params: FlagsCellRendererParams): FlagDisplayItem[] {
    const data = params.data ?? {};
    const configs = params.flagsConfig ?? [];

    return configs.map((config) => {
      const label = config.header_name || config.db_field;
      const value = this.toBoolean(data[config.db_field]);
      const isTechnicianFlag = this.isTechnicianFlag(config, label);
      const state = this.resolveState(isTechnicianFlag, value, data);

      return {
        config,
        dbField: config.db_field,
        label,
        shortLabel: this.buildShortLabel(label),
        tone: this.detectTone(label),
        state,
        iconClass: this.iconClassForState(state),
        iconState: this.iconStateForState(state),
        value,
        editable: !isTechnicianFlag && !!config.is_editable,
      };
    });
  }

  private isTechnicianFlag(config: DynamicGridConfigItem, label: string): boolean {
    return config.db_field === 'v_lading_site_checks.technician_required'
      || label.toLowerCase().includes('technician')
      || label.toLowerCase().includes('tech');
  }

  private resolveState(
    isTechnicianFlag: boolean,
    value: boolean,
    data: Record<string, any>,
  ): FlagDisplayItem['state'] {
    if (!isTechnicianFlag) return value ? 'on' : 'off';
    if (!value) return 'not-required';
    return this.hasTechnicianAssignment(data) ? 'required-set' : 'required';
  }

  private hasTechnicianAssignment(data: Record<string, any>): boolean {
    const assignment = data['__technician_assignment'];
    return assignment !== null
      && assignment !== undefined
      && String(assignment).trim() !== '';
  }

  private iconClassForState(state: FlagDisplayItem['state']): string {
    if (state === 'not-required') return 'fa-xmark';
    if (state === 'required') return 'fa-triangle-exclamation';
    if (state === 'required-set' || state === 'on') return 'fa-check';
    return 'fa-xmark';
  }

  private iconStateForState(state: FlagDisplayItem['state']): FlagDisplayItem['iconState'] {
    if (state === 'not-required') return 'is-x';
    if (state === 'required') return 'is-required';
    if (state === 'required-set' || state === 'on') return 'is-check';
    return 'is-x';
  }

  private buildShortLabel(label: string): string {
    const normalized = label.trim().toLowerCase();

    if (normalized.includes('customer') && normalized.includes('confirm')) return 'Cust';
    if (normalized.includes('survey') && (normalized.includes('file') || normalized.includes('complete'))) return 'Filed';
    if (normalized.includes('survey') && normalized.includes('required')) return 'Req';
    if (normalized.includes('survey')) return 'Survey';
    if (normalized.includes('technician') || normalized.includes('tech')) return 'Tech';

    const words = label
      .split(/[\s/_-]+/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (words.length >= 2) {
      return words
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('');
    }

    return (words[0] ?? label).slice(0, 5);
  }

  private detectTone(label: string): FlagDisplayItem['tone'] {
    const normalized = label.toLowerCase();

    if (normalized.includes('customer') && normalized.includes('confirm')) return 'blue';
    if (normalized.includes('survey') && (normalized.includes('file') || normalized.includes('complete'))) return 'emerald';
    if (normalized.includes('survey')) return 'amber';
    if (normalized.includes('technician') || normalized.includes('tech')) return 'violet';
    return 'slate';
  }

  private toBoolean(raw: any): boolean {
    return raw === true || raw === 'true' || raw === 't' || raw === '1' || raw === 1;
  }
}
